import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

interface PageHeroMetric {
  label: string;
  value: string;
}

interface PageHeroProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  eyebrow?: string;
  metrics?: PageHeroMetric[];
  actions?: ReactNode;
  className?: string;
}

export function PageHero({
  title,
  subtitle,
  icon,
  eyebrow,
  metrics,
  actions,
  className,
}: PageHeroProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-4 shadow-md sm:p-6',
        className,
      )}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4 min-w-0">
          {icon ? (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              {icon}
            </span>
          ) : null}
          <div className="min-w-0">
            {eyebrow ? (
              <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="text-2xl font-bold text-foreground truncate">{title}</h1>
            {subtitle ? <p className="text-sm text-muted-foreground mt-1">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {metrics && metrics.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:mt-6 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="rounded-lg border border-border/60 bg-background/60 px-4 py-3"
            >
              <p className="text-xs text-muted-foreground">{m.label}</p>
              <p className="text-lg font-semibold tabular-nums text-foreground mt-0.5 truncate">
                {m.value}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
